import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { AccountInfoModel, CartModel, CompanyValidatorModel } from 'src/app/activation-workflow/shared/state/activation-workflow.model';
import { CORE_CONFIGURATION } from '../../core/configuration';
import { ApiModule } from './api.module';
import { BaseApiService } from './base-api.service';
import { LoggerService, SessionState, SessionStateModel, buildAwsApi } from '@myvf/core';
import { Store } from '@ngxs/store';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FeatureFlagsService } from './featureFlags/feature-flags.service';

@Injectable({
  providedIn: ApiModule,
  deps: [BaseApiService],
})
export class WorkflowApiService {
  private usernameRole = 'web account';
  keyMapAWS: any;
  
  private readonly processFlowHost$: Promise<string>;

  constructor(
    private http: BaseApiService,
    private store: Store,
    @Inject(CORE_CONFIGURATION) private config,
    private logger: LoggerService,
    private featureFlags: FeatureFlagsService
  ) {
    const { keyMapAWS }: SessionStateModel = this.store.selectSnapshot(SessionState);
    this.keyMapAWS = keyMapAWS;

    this.processFlowHost$ = this.featureFlags
      .check('processFlowEksMigration')
      .then(isEnabled => (isEnabled ? environment.apiAWS : environment.api))
      .catch(() => environment.api);
  }

  private withProcessFlowHost<T>(call: (host: string) => Observable<T>): Observable<T> {
    return from(this.processFlowHost$).pipe(switchMap(call));
  }

  /**
   * @param technology ex.'VULA'
   * @param requestType 'NL' or 'GNP'
   * @returns true if appointments for the given technology are available false otherwise
   */
  appointmentAvailability(technology:string,requestType:string){
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_appointmentCheck',{technology:technology,requestType:requestType});
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      path: apiAWS,
    });
  }

  /**
   * get a list of appointments for the given technology
   * @param techonology
   * @param province
   * @param numberMaxSlot number of appointmnmets, default 124 and min 1/max 124
   * @param buildingId
   * @param operator
   * @param scalaId
   * @param serviceType
   * @returns list of appointments
   */
  appointmentSlots(technology:string,province:string,numberMaxSlot:number,buildingId:string='',operator:string='',scalaId:string='',serviceType:string='',technologyType=null){
    const apiAWS = buildAwsApi( this.keyMapAWS, 'activation_appointmentSlot', {
      technology: technology,
      province: province,
      numberMaxSlot: numberMaxSlot,
      buildingId: buildingId,
      operator: operator,
      scalaId: scalaId,
      serviceType: serviceType,
      technologyType: technologyType
    });
    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      path: apiAWS,
    });
  }


  /**
   *
   * @param slotId
   * @param technology
   * @param serviceType
   * @param operator
   * @param slotType
   * @param buildingId
   * @param scalaId
   * @param province
   * @param vatOrCf
   * @param addressCode
   * @param streetNumber
   * @param posCode
   * @param referentName
   * @param fullAddress
   * @param contactPhone
   * @param contactEmail
   * @param technologyType
   * @returns
   */
  appointmentSchedule(
    slotId:number,
    technology:string,
    serviceType:string='',
    operator:string='',
    slotType:string,
    buildingId:string,
    scalaId:string,
    province:string,
    vatOrCf:string,
    addressCode:string,
    streetNumber:string,
    posCode:string,
    referentName:string,
    fullAddress:string,
    contactPhone:number,
    contactEmail:string,
    technologyType=null){
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_appointmentSchedule',{});
    const body = {
      slotId: slotId,
      technology: technology,
      serviceType: serviceType,
      operator: operator,
      slotType: slotType,
      buildingId: buildingId,
      scalaId: scalaId,
      province: province,
      vatOrCf: vatOrCf,
      addressCode: addressCode,
      streetNumber: streetNumber,
      posCode: posCode,
      referentName: referentName,
      fullAddress: fullAddress,
      contactPhone: contactPhone,
      contactEmail: contactEmail,
      technologyType: technologyType
    };
    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      path: apiAWS,
      body:body
    });
  }

  /**
   * accountInfo get account info such as shipping addresses, payment methods, billing informations
   * @param customerId The code of the customer
   */
  accountInfo(customerId: string): Observable<any> {
    const apiAWS = buildAwsApi(this.keyMapAWS,'account_info',{customerId});

    return this.http.get({
      apiBasePath: this.config.apiBasePath,
      awsApi: apiAWS !== null,
      path: apiAWS ?? '/v2//' + customerId,
    });
  }

  /**
   * accountInfo get company validators
   * @param custCode The code of the customer
   */
  companyValidators(custCode: string): Observable<any> {
    let params = new HttpParams();
    params = params.append('relatedParty_id', custCode);
    params = params.append('relatedParty_name', 'custCodeRoot');
    params = params.append('status', 'A'); // only active validators

    return this.http.get({
      apiBasePath: '',
      path: '/partymanagementlegacy/v2/individual/',
      params,
    });
  }

  /**
   * POST method on DXL: processFlow/createCart
   * @param localCart info of the cart
   * @param productType info of the offer if it is mobile or fixed
   * @returns id of the cart
   */
  createCart(localCart: CartModel, username: string, productType: string = 'MOBILE'): Observable<any> {

    // if keyMapAWS is present, we should use it!
    const apiAWS = buildAwsApi(this.keyMapAWS,'activation_shoppingcart_post', {});

    const body = {
      cartItem: [
        {
          status: 'active',
          quantity: 1,
          productOffering: {
            id: localCart.offer.offerId,
            '@referredType': localCart.offer.type,
          },
          product: {
            productSpecification: {
              id: localCart.offer.id,
              name: localCart.offer.title
            },
            productCharacteristic: [
              {
                name: '',
                value: ''
              }
            ], // facoltativo
            //*Da decommentare quando si introdurrà l'acquisto di un device nel workflow*
            // realizingResource: [], // facoltativo
            product: [], // facoltativo
          },
        },
      ],
      contactMedium: [],
      relatedParty: [
        {
          id: username,
          role: 'web account',
        },
      ],
    };

    if (productType === 'MOBILE') {
      body.contactMedium.push(
        {
          characteristic: {
            phoneNumber: localCart.contactNumber,
          },
        }
      );

      body.cartItem[0].productOffering = {
        id: localCart.offer.offerId,
        '@referredType': localCart.offer.type,
      };

      //*Da decommentare quando si introdurrà l'acquisto di un device nel workflow*
      // Added device to shoppinCart body
      // if(!!localCart.device) {
      //   body.cartItem[0].product.realizingResource.push({
      //     id: localCart.device.id,
      //     name:  localCart.device.variation.title,
      //     '@referredType': 'PhysicalResourceSpec'
      //   });
      // };

      if (localCart.portabilityType === 'EXISTING_NUMBER') {
        // The user has chosen portability
        body.cartItem[0].product.productCharacteristic[0].name = 'MNP';
        body.cartItem[0].product.productCharacteristic[0].value = 'true';
      }
    }

    if (productType === 'FIXED') {

      body.cartItem[0].productOffering = {
        id: localCart.offer.offerId,
        '@referredType': 'FIXED',
      };

      body.contactMedium.push(
        {
          '@type': 'GeographicAddress',
          mediumType: 'installation address',
          preferred: true,
          characteristic: {
            city: localCart.shippingAddress.city,
            postCode: localCart.shippingAddress.postCode,
            socialNetworkId: localCart.shippingAddress.addressCode, // localCart.offer.offerId
            stateOrProvince: localCart.shippingAddress.stateOrProvince,
            street2: 'VIA',
            street1: localCart.shippingAddress.street + '|' + localCart.shippingAddress.streetNr
          }
        });

      if (localCart.portabilityType === 'EXISTING_NUMBER') {
        // The user has chosen portability
        body.cartItem[0].product.productCharacteristic[0].name = 'GNP';
        body.cartItem[0].product.productCharacteristic[0].value = 'true';
      }

      body.cartItem[0].product.product.push({
        realizingService:[{
          id: localCart.coverage.idPackage,
          name: localCart.coverage.specificTech
        }]
      });

      if(localCart.coverage?.addOns) {
        // The offer has extra services
        localCart.coverage?.addOns.forEach( addOn => {
          body.cartItem[0].product.product.push({
            productSpecification: {
              id: addOn.id,
              name: addOn.title,
              '@referredType': 'AddOn'
            }
          });
        });
      }
    }

    return this.http.post({
      apiBasePath: this.config.apiBasePath,
      path: apiAWS ?? '/v2/products-catalog/shoppingCart',
      awsApi: apiAWS !== null,
      body,
    });
  }

  /**
   * POST method on DXL: processFlow/createProcessFlow
   * @param shoppingCartId ID of shopping cart
   * @param vatCode pIva
   * @param username Username of referent account
   * @param custCode custCode of logged in account
   * @returns promise of createProcessFlow
   */
  createProcessFlow(
    shoppingCartId: string,
    vatCode: string,
    username: string,
    custCode: string,
    hideError: boolean = false
  ): Observable<any> {
    const body = {
      processFlowSpecification: 'OrderCapture',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      relatedEntity: [
        {
          id: shoppingCartId,
          role: 'ShoppingCart',
        },
      ],
      relatedParty: [
        {
          id: vatCode,
          role: 'VATCode',
        },
        {
          id: username,
          role: this.usernameRole,
        },
      ],
      productOrder: [
        {
          relatedParty: [
            {
              id: custCode,
              role: 'Account',
            },
          ],
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.post({
      apiBasePath: '',
      host,
      path: '/activation/v1/processFlow',
      body,
      hideError,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/mobileInfo
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @param state completed or cancelled
   * @returns promise of PATCH DXL processFlow/mobileInfo
   */
  updateMobileInfo(processFlowId: string, localCart: CartModel, vatCode: string, username: string, state: string): Observable<any> {
    const body: TaskFlowModel = {
      id: 'mobileInfo',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      channel: [{ id: 'APP', name: 'APP' }],
      state: state,
      productOrder: {
        productOrderItem: [
          {
            id: localCart.offer.offerId,
            product: {
              id: localCart.offer.id,
              realizingResource: [
                {
                  id: '', // Number to migrate
                  value: '', // Serial number of SIM we want to migrate + pipe + simtype
                },
              ],
            },
          },
        ],
      },
      relatedParty: [
        {
          id: vatCode,
          role: 'VATCode',
        },
        {
          id: username,
          role: this.usernameRole,
        },
      ],
    };

    if(localCart.productType === 'MOBILE') {
      if ( localCart.portabilityType === 'EXISTING_NUMBER') {
        // Number to migrate
        body.productOrder.productOrderItem[0].product.realizingResource[0].id = localCart.numberToMigrate;
        // SIM serial number
        body.productOrder.productOrderItem[0].product.realizingResource[0].value += localCart.simSerialNumber;
        // Billing plan type
        body.productOrder.productOrderItem[0].product.realizingResource[0]['@type'] =
          localCart.billingPlanType === 'PREPAID' ? 'PrepaidLogicalResource' : 'PostpaidLogicalResource';
        // Origin carrier
        body.productOrder.productOrderItem[0].product.realizingResource[0].name = localCart.originalCarrier;
        // Referred type
        body.productOrder.productOrderItem[0].product.realizingResource[0]['@referredType'] = 'Mobile';
      }

      if (localCart.simType === 'STANDARD') {
        body.productOrder.productOrderItem[0].product.realizingResource[0].value += '|removable';
      } else if (localCart.simType === 'ESIM') {
        body.productOrder.productOrderItem[0].product.realizingResource[0].value += '|embedded';
      }
    }

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/mobileInfo`,
      body,
      globalErrorHandler: false,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/GNP
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @param state completed or cancelled
   * @returns promise of PATCH DXL processFlow/GNP
   */
  _updateGNPInfo(
    processFlowId: string,
    vatCode: string,
    username: string,
    state: string,
    localCart: CartModel): Observable<any> {

    const body: TaskFlowModel = {
      id: 'GNP',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: state,
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        productOrderItem: [
          {
            id: localCart.offer.offerId,
            product: {
              id: localCart.offer.id,
              realizingResource: [
                {
                  id: '',
                  value: ''
                }
              ],
              productCharacteristic: [
                {
                  isISDN: ''
                }
              ]

            }
          }
        ]
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: vatCode,
          role: 'VATCode',
        },
      ],
    };

    if(!!localCart && localCart.productType === 'FIXED' && localCart?.portabilityType === 'EXISTING_NUMBER') {
      body.productOrder.productOrderItem[0].product.realizingResource[0].id = localCart.prefix + '|' + localCart.numberToMigrate;
      body.productOrder.productOrderItem[0].product.realizingResource[0].name = localCart.originalCarrier  + '|' + localCart.originalCarrier;
      body.productOrder.productOrderItem[0].product.realizingResource[0].value = localCart.migrationCode  + '|' + localCart.migrationCode;
      body.productOrder.productOrderItem[0].product.realizingResource[0]['@type'] = 'CompositeLogicalResource';
      body.productOrder.productOrderItem[0].product.realizingResource[0]['@referredType']  = 'Fixed';

      body.productOrder.productOrderItem[0].product.productCharacteristic[0].isISDN = localCart.haveISDN ? 'Y' : 'N';
    }

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/GNP`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/GNP
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @param state completed or cancelled
   * @returns promise of PATCH DXL processFlow/GNP
   */
  updateGNPInfo(
    processFlowId: string,
    vatCode: string,
    username: string,
    state: string,
    localCart: CartModel): Observable<any> {

    let body: TaskFlowModel = {
      id: 'GNP',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: state,
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: vatCode,
          role: 'VATCode',
        },
      ],
    };

    if(!!localCart && localCart.productType === 'FIXED' && localCart?.portabilityType === 'EXISTING_NUMBER') {
      // body.state = 'completed';
      body = {
        ...body,
        productOrder: {
          productOrderItem: [
            {
              id: localCart.offer.offerId,
              product: {
                id: localCart.offer.id,
                realizingResource: [
                  {
                    id: localCart.prefix + '|' + localCart.numberToMigrate,
                    name: localCart.originalCarrier  + '|' + localCart.originalCarrier,
                    value: localCart.migrationCode  + '|' + localCart.migrationCode,
                    '@type': 'CompositeLogicalResource',
                    '@referredType': 'Fixed'
                  }
                ],
                productCharacteristic: [
                  {
                    name:'isISDN',
                    value: localCart.haveISDN ? 'Y' : 'N'
                    // isISDN: localCart.haveISDN ? 'Y' : 'N'
                  }
                ]

              }
            }
          ]
        }
      };
    }

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/GNP`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/OTP
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param validator Validator information
   * @param accountInfo Account info of company referent
   * @param customer info of referent account
   * @returns promise of PATCH DXL processFlow/OTP
   */
  requestOTP(
    processFlowId: string,
    validator: CompanyValidatorModel,
    accountInfo: AccountInfoModel,
    customer: any
  ): Observable<any> {

    // Format the invoicing address
    const rootCustcode = validator.relatedParty.find((element) => element.name === 'custCodeRoot').id;

    const body: TaskFlowModel = {
      id: 'OTP',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'hold',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            characteristic: [
              {
                firstName: validator.givenName,
                lastName: validator.familyName,
                contactNumber: validator.contactMedium[0].medium.number,
                emailAddress: validator.contactMedium[0].medium.emailAddress,
                rootCustcode: rootCustcode
              },
            ],
            id: validator.id,
            role: 'RefValidator'
          },
        ],
        agreement: [
          {
            name: 'OTP',
            '@type': 'MNP',
            '@referredType': 'Send'
          }
        ],
      },
      relatedParty: [
        {
          id: customer.username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/OTP`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/OTP
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param validator Validator information
   * @param accountInfo Account info of company referent
   * @param customer info of referent account
   * @param otpCode otp submitted by user
   * @returns promise of PATCH DXL processFlow/OTP
   */
  validateOTP(
    processFlowId: string,
    validator: CompanyValidatorModel,
    accountInfo: AccountInfoModel,
    customer: any,
    otpCode: string
  ): Observable<any> {

    // Format the invoicing address
    const rootCustcode = validator.relatedParty.find((element) => element.name === 'custCodeRoot').id;

    const body: TaskFlowModel = {
      id: 'OTP',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            characteristic: [
              {
                firstName: validator.givenName,
                lastName: validator.familyName,
                contactNumber: validator.contactMedium[0].medium.number,
                emailAddress: validator.contactMedium[0].medium.emailAddress,
                rootCustcode: rootCustcode
              },
            ],
            id: validator.id,
            role: 'RefValidator'
          },
        ],
        agreement: [
          {
            name: 'OTP',
            id: otpCode,
            '@type': 'MNP',
          }
        ],
      },
      relatedParty: [
        {
          id: customer.username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/OTP`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/companyInfo
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param accountInfo Account info of company referent
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/companyInfo
   */
  updateCompanyInfo(
    processFlowId: string,
    localCart: CartModel,
    accountInfo: AccountInfoModel,
    username: string
  ): Observable<any> {
    // Format the legal address
    const filterAddress = accountInfo.customerDetails.addresses.filter((element) => element.type === 'INVOICING');
    const legalAddress = filterAddress[filterAddress.length-1];

    if (!legalAddress) {
      this.logger.error(new Error('No legal address found for this user'));
      return throwError({ message: 'legalAddressMissing', reason: 'No Legal Address' });
    }

    const formattedLegalAddress = `${legalAddress.street || ''}|${legalAddress.streetNr || 'snc'}||${
      legalAddress.city || ''
    }|${legalAddress.postCode || ''}|${legalAddress.stateOrProvince || ''}`;

    // Format the invoicing address
    const invoicingAddress = legalAddress;

    const formattedInvoicingAddress = `${invoicingAddress.street || ''}|${invoicingAddress.streetNr || 'snc'}||${
      invoicingAddress.city || ''
    }|${invoicingAddress.postCode || ''}|${invoicingAddress.stateOrProvince || ''}`;

    const body: TaskFlowModel = {
      id: 'companyInfo',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            characteristic: [
              {
                contactNumber: localCart.contactNumber.substring(0, 3) + '|' + localCart.contactNumber.substring(3),
                businessName: accountInfo.customerDetails.companyName,
                ivaCode: accountInfo.customerDetails.pIva,
                legalStatus: accountInfo.customerDetails.lastName,
                legalAddress: formattedLegalAddress,
                invoicingDomiciliation: formattedInvoicingAddress,
                csLevel: '10',
              },
            ],
            role: 'Account',
          },
        ],
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/companyInfo`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/referentInfo
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param accountInfo Account info of company referent
   * @param customer info of referent account
   * @returns promise of PATCH DXL processFlow/referentInfo
   */
  updateReferentInfo(
    processFlowId: string,
    localCart: CartModel,
    accountInfo: AccountInfoModel,
    customer: any
  ): Observable<any> {
    let attachmentType: number;
    switch (localCart.document.type) {
    case 'IDENTITY_CARD':
      attachmentType = 1;
      break;
    case 'PASSPORT':
      attachmentType = 2;
      break;
    case 'DRIVING_LICENSE':
      attachmentType = 3;
      break;
    } // endswitch

    const body: TaskFlowModel = {
      id: 'referentInfo',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            characteristic: [
              {
                firstName: localCart.name,
                lastName: localCart.surname,
                emailAddress: localCart.email,
                contactNumber: localCart.contactNumber.substring(0, 3) + '|' + localCart.contactNumber.substring(3),
                fiscalCode: localCart.fiscalCode,
                cityOfBirth: localCart.birthCity,
                placeOfBirth: localCart.birthProvince,
                gender: localCart.gender.toLowerCase(),
                attachmentType: attachmentType,
                attachmentNumber: localCart.document.documentId,
              },
            ],
            role: 'RefPda',
          },
        ],
      },
      relatedParty: [
        {
          id: customer.username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/referentInfo`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/checkOCR
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/checkOCR
   */
  updateCheckOCR(processFlowId: string, localCart: CartModel, vatCode: string, username: string): Observable<any> {
    const body: TaskFlowModel = {
      id: 'checkOCR',
      processFlowId: processFlowId,
      completionMethod: 'uploadDocument',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        attachment: [
          {
            attachmentType: 'FrontDocument',
            content: localCart.document.attachmentFrontside,
            mimeType: localCart.document.attachmentFrontsideType,
          },
          {
            attachmentType: 'RetroDocument',
            content: localCart.document?.attachmentBackside ?? '',
            mimeType: localCart.document?.attachmentBacksideType ?? '',
          },
        ],
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: vatCode,
          role: 'VATCode',
        },
      ],
    };

    // eslint-disable-next-line
    // if (localCart.document.type === 'IDENTITY_CARD') {
    //   body.productOrder.attachment.push(
    //     {
    //       attachmentType: 'RetroDocument',
    //       content: localCart.document.attachmentBackside,
    //       mimeType: localCart.document.attachmentBacksideType,
    //     }
    //   );
    // } // endif

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/checkOCR`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/consents
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param accountInfo Account info of company referent
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/consents
   */
  updateConsents(
    processFlowId: string,
    localCart: CartModel,
    accountInfo: AccountInfoModel,
    username: string
  ): Observable<any> {
    const body: TaskFlowModel = {
      id: 'consents',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            id: accountInfo.customerCode,
            role: 'Account',
            '@referredType': 'Organization',
            characteristic: [
              {
                commercialPrivacyProfile: localCart.consents?.commercialPrivacyProfile ?? 'N',
                profilingPrivacyProfile: localCart.consents?.profilingPrivacyProfile ?? 'N',
                localizationPrivacyProfile: localCart.consents?.localizationPrivacyProfile ?? 'N',
                dataEnrichmentPrivacyProfile: localCart.consents?.dataEnrichmentPrivacyProfile ?? 'N',
                thirdPartiesPrivacyProfile: localCart.consents?.thirdPartiesPrivacyProfile ?? 'N',
              },
            ],
          },
        ],
        characteristic: [
          {
            cgc: localCart.consents?.cgc ?? 'Y',
            clausoleVessatorie: localCart.consents?.clausoleVessatorie ?? 'Y',
          },
        ],
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/consents`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/domainInfo
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/domainInfo
   */
  updateDomainInfo(processFlowId: string, username: string): Observable<any> {
    const body: TaskFlowModel = {
      id: 'domainInfo',
      processFlowId: processFlowId,
      state: 'cancelled',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/domainInfo`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/shippingInfo
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param localCart Cart information
   * @param accountInfo Account info of company referent
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/shippingInfo
   */
  updateShippingInfo(
    processFlowId: string,
    localCart: CartModel,
    accountInfo: AccountInfoModel,
    username: string
  ): Observable<any> {
    // Format the legal address
    const filterAddress = accountInfo.customerDetails.addresses.filter((element) => element.type === 'INVOICING');
    const legalAddress = filterAddress[filterAddress.length-1];

    // Format the shipping address
    const shippingAddress = localCart.shippingAddress;
    const formattedShippingAddress = `${shippingAddress.street || ''}|${shippingAddress.streetNr || ''}||${
      shippingAddress.city || ''
    }|${shippingAddress.postCode || ''}|${shippingAddress.stateOrProvince || ''}`;
    // Format the billing address
    const billingAddress = legalAddress;
    const formattedBillingAddress = `${billingAddress.street || ''}|${billingAddress.streetNr || ''}||${
      billingAddress.city || ''
    }|${billingAddress.postCode || ''}|${billingAddress.stateOrProvince || ''}`;

    const body: TaskFlowModel = {
      id: 'shippingInfo',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        relatedParty: [
          {
            characteristic: [
              {
                shippingAddress: formattedShippingAddress,
                billingAddress: formattedBillingAddress,
                isBilling: formattedBillingAddress === formattedShippingAddress ? 'Y' : 'N',
              },
            ],
            role: 'Account',
          },
        ],
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/shippingInfo`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/billingOptions
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param accountInfo Account info of company referent
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/billingOptions
   */
  updateBillingOptions(processFlowId: string, accountInfo: AccountInfoModel, username: string): Observable<any> {
    // Format the legal address
    const filterAddress = accountInfo.customerDetails.addresses.filter((element) => element.type === 'INVOICING');
    const legalAddress = filterAddress[filterAddress.length-1];

    // Format the invoicing address
    const invoicingAddress = legalAddress;
    const formattedInvoicingAddress = `${invoicingAddress.street || ''}|${invoicingAddress.streetNr || ''}||${
      invoicingAddress.city || ''
    }|${invoicingAddress.postCode || ''}|${invoicingAddress.stateOrProvince || ''}`;

    const body: TaskFlowModel = {
      id: 'billingOptions',
      processFlowId: processFlowId,
      completionMethod: 'userInput',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      productOrder: {
        characteristic: [
          {
            invoicingAddress: formattedInvoicingAddress,
            billingOption: accountInfo.billingAccounts[0].billingOptions[0]?.name ?? 'B', // Default is both: printed and email
            paymentMethod: accountInfo.billingAccounts[0].defaultPaymentMethod.id,
          },
        ],
      },
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: accountInfo.customerDetails.pIva,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}/taskFlow/billingOptions`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow/$processFlowId
   * @param processFlowId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow/$processFlowId
   */
  completeOrder(processFlowId: string, vatCode: string, username: string): Observable<any> {
    const body: TaskFlowModel = {
      id: processFlowId,
      processFlowSpecification: 'OrderCapture',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: vatCode,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.patch({
      apiBasePath: '',
      host,
      path: `/activation/v1/processFlow/${processFlowId}`,
      body,
    }));
  }

  /**
   * PATCH method on DXL: processFlow
   * @param dxlOrderId Identifier of the parent ProcessFlow entity (Mandatory)
   * @param vatCode VatCode information
   * @param username Username of referent account
   * @returns promise of PATCH DXL processFlow
   */
  orderFulfillment(dxlOrderId: string, vatCode: string, username: string): Observable<any> {
    const body: TaskFlowModel = {
      processFlowSpecification: 'OrderFulfillment',
      state: 'completed',
      channel: [
        {
          id: 'APP',
          name: 'APP',
        },
      ],
      relatedEntity: [
        {
          id: dxlOrderId,
          role: 'ProductOrder',
        },
      ],
      relatedParty: [
        {
          id: username,
          role: this.usernameRole,
        },
        {
          id: vatCode,
          role: 'VATCode',
        },
      ],
    };

    return this.withProcessFlowHost(host => this.http.post({
      apiBasePath: '',
      host,
      path: '/activation/v1/processFlow',
      body,
    }));
  }

  /**
   * It gets status of orderFulfillment
   * @param processFlowId Identifier of the parent ProcessFlow entity
   * @param orderFulfillmentId Identifier of the ProcessFlow orderFulfillment
   */
  orderFulfillmentStatus(orderFulfillmentId: string): Observable<any> {
    let params = new HttpParams();
    params = params.set('process_flow_specification', 'OrderFulfillment');

    return this.withProcessFlowHost(host => this.http.get({
      apiBasePath: '',
      host,
      path: '/activation/v1/processFlow/' + orderFulfillmentId,
      params: params
    })).pipe(
      catchError((err) => {return { ...err, state: 'GENERIC_ERROR' };})
    );
  }
}

interface ChannelModel {
  id: string; // in this case is APP
  name: string; // in this case is APP
}

interface CharacteristicModel {
  name?: string; // Name of the charateristic
  value?: string; // Value of the charateristic
  legalAddress?: string; // e.g.: streetName|streetNr|locality|city|postcode|stateOrProvince
  invoicingDomiciliation?: string; // e.g.: streetName|streetNr|locality|city|postcode|stateOrProvince
  invoicingAddress?: string; // e.g.: streetName|streetNr|locality|city|postcode|stateOrProvince
  shippingAddress?: string; // e.g.: streetName|streetNr|locality|city|postcode|stateOrProvince
  billingAddress?: string; // e.g.: streetName|streetNr|locality|city|postcode|stateOrProvince
  isBilling?: string; // e.g.: Y | N
  billingOption?: string;
  paymentMethod?: string;
  contactNumber?: string; // e.g.: 0332123456
  ivaCode?: string; // e.g.: 07643520567
  legalStatus?: string; // e.g.: S.p.A
  businessName?: string; // e.g.: Vodafone
  csLevel?: string; // e.g.: 10
  firstName?: string; // e.g.: Mario
  lastName?: string; // e.g.: Rossi
  emailAddress?: string; // e.g.: mario.rossi@vodafone.it
  rootCustcode?: string;
  fiscalCode?: string; // e.g.: pttgrl85c10c351c
  cityOfBirth?: string; // e.g.: Milano
  placeOfBirth?: string; // e.g.: MI
  gender?: string; // f | m
  attachmentType?: number; // 1 (identity card) | 2 (passport) | 3 (driving license)
  birthDate?: string; // e.g.: 31/08/1978
  attachmentNumber?: string; // e.g.: as1234567
  // Consents (value: Y | N)
  commercialPrivacyProfile?: string;
  profilingPrivacyProfile?: string;
  localizationPrivacyProfile?: string;
  dataEnrichmentPrivacyProfile?: string;
  thirdPartiesPrivacyProfile?: string;
  cgc?: string;
  clausoleVessatorie?: string;
}

interface RelatedPartyModel {
  id?: string; // VAT code or username for CB users
  role?: string; // To be set to "VATCode" static value or "web account" when id is the username of a CB user
  '@referredType'?: string; // e.g.: Organization
  characteristic?: Array<CharacteristicModel>;
}

interface AttachmentModel {
  attachmentType: string; // FrontDocument | RetroDocument
  content: string; // Base64Code
  mimeType: string; // Type of the attachment
}

export interface TaskFlowModel {
  id?: string; // task ID
  processFlowId?: string; // Process Flow ID from POST process/flow
  processFlowSpecification?: string;
  channel?: Array<ChannelModel>; // A list of channel(s) where this taskFlow is executed: default is "APP"
  completionMethod?: string; // TaskFlow completion method. Const: userInput
  state?: string; // lifecycle state of the taskFlow: active, cancelled, hold, completed
  relatedEntity?: Array<any>; // A list of related entity(ies) to this taskFlow (optional)
  characteristic?: Array<any>; //
  relatedParty?: Array<RelatedPartyModel>; // A list of related party(ies) to this task
  productOrder?: {
    attachment?: Array<AttachmentModel>; // A list of attachments related to this task
    relatedParty?: Array<RelatedPartyModel>; // A list of related party(ies) to this task
    productOrderItem?: Array<{
      id?: string | number;
      product?: any;
    }>;
    characteristic?: Array<CharacteristicModel>;
    agreement?: [{
      name?: string;
      id?: string;
      '@type'?: string;
      '@referredType'?: string;
    }]
  }; // A list of productOrder entity (each task has its own properties)
  taskFlowRelationship?: Array<any>; // Describes relationship between taskFlow (optional)
}
